import LegalDocumentAcceptDialog from "@/components/legal/LegalDocumentAcceptDialog";
import { REGISTRATION_CONSENT_TRANSLATIONS } from "@/components/auth/authShellConstants";
import {
    getLegalEffectiveDate,
    getParentRoleDeclaration,
    getPrivacySectionsForLocale,
    getSecretaryRoleDeclaration,
    getTermsSectionsForLocale,
    getTherapistRoleDeclaration,
    LEGAL_PRIVACY_VERSION,
    LEGAL_TERMS_VERSION,
    resolveTherapistRegistrationNotice,
} from "@/domains/shared/constants/legalDocuments";
import type { UserRole } from "@/domains/shared/models/roles";
import { Roles } from "@/domains/shared/models/roles";
import { SubscriptionPlanIds, type SubscriptionPlanDefinition, type SubscriptionPlanId } from "@/domains/subscription/plans";
import { useSubscriptionPlans } from "@/domains/subscription/useSubscriptionPlans";
import { getTranslation, type MivoTranslationType } from "@mivo/i18n";
import { useState } from "react";

export type RegistrationConsentState = {
    acceptedTerms: boolean;
    acceptedPrivacy: boolean;
    acceptedRoleDeclaration: boolean;
};

type RegistrationConsentBlockProps = {
    role: UserRole;
    consent: RegistrationConsentState;
    onConsentChange: (next: RegistrationConsentState) => void;
    testIdPrefix?: string;
    intendedPlanId?: SubscriptionPlanId | null;
    referralActive?: boolean;
    referralPlanId?: SubscriptionPlanId | null;
    /** When false, hides the solo-therapist plan/trial notice (e.g. clinic manager signup). Default true for therapists. */
    showPlanNotice?: boolean;
    /** Overrides the default role declaration checkbox text. */
    roleDeclarationOverride?: string | MivoTranslationType;
};

export default function RegistrationConsentBlock({
    role,
    consent,
    onConsentChange,
    testIdPrefix = "register",
    intendedPlanId,
    referralActive = false,
    referralPlanId,
    showPlanNotice = true,
    roleDeclarationOverride,
}: RegistrationConsentBlockProps) {
    const roleDeclaration =
        typeof roleDeclarationOverride === "string"
            ? roleDeclarationOverride
            : roleDeclarationOverride
                ? getTranslation(roleDeclarationOverride)
                : role === Roles.Therapist
                    ? getTherapistRoleDeclaration()
                    : role === Roles.Secretary
                        ? getSecretaryRoleDeclaration()
                        : getParentRoleDeclaration();

    const [termsDialogOpen, setTermsDialogOpen] = useState(false);
    const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
    const plansQuery = useSubscriptionPlans();
    const registrationNotice = resolveTherapistRegistrationNotice(
        intendedPlanId,
        plansQuery.data,
        plansQuery.isSuccess,
        referralActive && referralPlanId ? { active: true, planId: referralPlanId } : undefined,
    );
    const termsSections = getTermsSectionsForLocale(
        undefined,
        plansQuery.data
            ?.filter(
                (plan: SubscriptionPlanDefinition) =>
                    plan.id === SubscriptionPlanIds.basic || plan.id === SubscriptionPlanIds.pro,
            )
            .map((plan: SubscriptionPlanDefinition) => ({
                name: plan.name,
                trialDays: plan.trialDays,
                patientLimit: plan.patientLimit,
            })),
    );
    const privacySections = getPrivacySectionsForLocale();

    const update = (patch: Partial<RegistrationConsentState>) => {
        onConsentChange({ ...consent, ...patch });
    };

    const openTermsDialog = () => setTermsDialogOpen(true);
    const openPrivacyDialog = () => setPrivacyDialogOpen(true);

    return (
        <div
            className="rounded-xl bg-muted/40 p-4 ring-1 ring-foreground/10 sm:p-5 space-y-4"
            data-testid={`${testIdPrefix}-consent-block`}
        >
            <p className="text-sm font-semibold text-foreground">
                {getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.sectionTitle)}
            </p>

            {role === Roles.Therapist && showPlanNotice && registrationNotice.status !== "unavailable" ? (
                <p
                    className="text-xs sm:text-sm text-destructive/90 leading-relaxed rounded-xl border border-destructive/70 bg-destructive/10 p-3"
                    data-testid={`${testIdPrefix}-trial-notice`}
                >
                    {registrationNotice.status === "loading"
                        ? getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.loadingPlan)
                        : registrationNotice.text}
                </p>
            ) : null}

            <div className="space-y-3">
                <DocumentConsentCheckbox
                    testId={`${testIdPrefix}-consent-terms`}
                    checked={consent.acceptedTerms}
                    onCheckedChange={(checked) => update({ acceptedTerms: checked })}
                    onOpenDocument={openTermsDialog}
                    label={
                        <>
                            {getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.acceptTermsPrefix)}{" "}
                            <DocumentLinkButton
                                onClick={openTermsDialog}
                                data-testid={`${testIdPrefix}-open-terms`}
                            >
                                {getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.termsOfUse)}
                            </DocumentLinkButton>{" "}
                            {getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.acceptTermsSuffix)}
                        </>
                    }
                />
                <DocumentConsentCheckbox
                    testId={`${testIdPrefix}-consent-privacy`}
                    checked={consent.acceptedPrivacy}
                    onCheckedChange={(checked) => update({ acceptedPrivacy: checked })}
                    onOpenDocument={openPrivacyDialog}
                    label={
                        <>
                            {getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.acceptPrivacyPrefix)}{" "}
                            <DocumentLinkButton
                                onClick={openPrivacyDialog}
                                data-testid={`${testIdPrefix}-open-privacy`}
                            >
                                {getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.privacyPolicy)}
                            </DocumentLinkButton>{" "}
                            {getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.acceptPrivacySuffix)}
                        </>
                    }
                />
                <ConsentCheckbox
                    testId={`${testIdPrefix}-consent-role`}
                    checked={consent.acceptedRoleDeclaration}
                    onChange={(checked) => update({ acceptedRoleDeclaration: checked })}
                    label={roleDeclaration}
                />
            </div>

            <LegalDocumentAcceptDialog
                open={termsDialogOpen}
                onOpenChange={setTermsDialogOpen}
                title={getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.termsOfUse)}
                description={getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.scrollToAccept)}
                sections={termsSections}
                version={LEGAL_TERMS_VERSION}
                effectiveDate={getLegalEffectiveDate()}
                onAccept={() => update({ acceptedTerms: true })}
                testIdPrefix={`${testIdPrefix}-terms`}
            />
            <LegalDocumentAcceptDialog
                open={privacyDialogOpen}
                onOpenChange={setPrivacyDialogOpen}
                title={getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.privacyPolicy)}
                description={getTranslation(REGISTRATION_CONSENT_TRANSLATIONS.scrollToAccept)}
                sections={privacySections}
                version={LEGAL_PRIVACY_VERSION}
                effectiveDate={getLegalEffectiveDate()}
                onAccept={() => update({ acceptedPrivacy: true })}
                testIdPrefix={`${testIdPrefix}-privacy`}
            />
        </div>
    );
}

type DocumentLinkButtonProps = {
    onClick: () => void;
    children: React.ReactNode;
    "data-testid"?: string;
};

function DocumentLinkButton({ onClick, children, "data-testid": testId }: DocumentLinkButtonProps) {
    return (
        <button
            type="button"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
            }}
            className="font-semibold underline hover:text-secondary-foreground text-inherit"
            data-testid={testId}
        >
            {children}
        </button>
    );
}

type DocumentConsentCheckboxProps = {
    testId: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    onOpenDocument: () => void;
    label: React.ReactNode;
};

function DocumentConsentCheckbox({
    testId,
    checked,
    onCheckedChange,
    onOpenDocument,
    label,
}: DocumentConsentCheckboxProps) {
    const handleCheckboxClick = (e: React.MouseEvent<HTMLInputElement>) => {
        e.preventDefault();

        if (checked) {
            onCheckedChange(false);
            return;
        }

        onOpenDocument();
    };

    const handleLabelTextClick = (e: React.MouseEvent<HTMLSpanElement>) => {
        const target = e.target as HTMLElement;

        if (target.closest("button")) {
            return;
        }

        onOpenDocument();
    };

    return (
        <label className="flex items-start gap-3 cursor-pointer text-sm text-primary leading-snug">
            <input
                type="checkbox"
                checked={checked}
                onChange={() => { }}
                onClick={handleCheckboxClick}
                required
                data-testid={testId}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
            />
            <span onClick={handleLabelTextClick}>{label}</span>
        </label>
    );
}

type ConsentCheckboxProps = {
    testId: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: React.ReactNode;
};

function ConsentCheckbox({ testId, checked, onChange, label }: ConsentCheckboxProps) {
    return (
        <label className="flex items-start gap-3 cursor-pointer text-sm text-primary leading-snug">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                required
                data-testid={testId}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
            />
            <span>{label}</span>
        </label>
    );
}
